import React, { useState, useEffect, useRef } from "react";
import { Toaster, toast } from "./components/ui/sonner";
import {
  Heart,
  Shield,
  ArrowRight,
  ArrowLeft,
  Check,
  Sparkles,
  AlertCircle,
  RefreshCw,
  Mail,
  User,
  Star,
  Award,
  ChevronRight,
  Lock,
  Printer,
  ChevronDown,
  Compass,
  CheckCircle2,
  Anchor,
  HelpCircle,
  Calendar,
  Share2,
  Timer,
  Users,
  Phone
} from "lucide-react";

// Questions exactly from design guidelines
const QUESTIONS_DATA = [
  {
    id: 1,
    category: "Emotional Trigger Reaction",
    question: "When a disagreement arises with your partner, what is your immediate internal response?",
    options: [
      { text: "I remain calm and address the core issue constructively without getting defensive.", points: 4 },
      { text: "I feel a momentary surge of anxiety, but usually pause before speaking.", points: 3 },
      { text: "I retreat into silence or shut down emotionally to protect myself from confrontation.", points: 2 },
      { text: "I immediately feel overwhelmed, defensive, or fearful of abandonment.", points: 1 }
    ]
  },
  {
    id: 2,
    category: "Communication Flow",
    question: "How comfortably do you express your deepest emotional needs and vulnerabilities?",
    options: [
      { text: "Effortlessly and authentically—I feel safe sharing my heart.", points: 4 },
      { text: "I express them after processing alone first, though it takes courage.", points: 3 },
      { text: "I often hint or wait for my partner to guess rather than stating them directly.", points: 2 },
      { text: "I swallow my needs completely because I fear being deemed burdensome or rejected.", points: 1 }
    ]
  },
  {
    id: 3,
    category: "Past Hurt & Resentment",
    question: "How often do past betrayals or emotional wounds linger in your current interactions?",
    options: [
      { text: "Rarely. I have processed past hurts and view current situations with fresh eyes.", points: 4 },
      { text: "Occasionally, but I actively catch myself and redirect my mindset.", points: 3 },
      { text: "Frequently. Old triggers resurface during stress, causing friction.", points: 2 },
      { text: "Constantly. I carry heavy invisible armor that prevents genuine emotional intimacy.", points: 1 }
    ]
  },
  {
    id: 4,
    category: "Self-Worth & Boundaries",
    question: "When setting personal boundaries in your relationship, how do you feel?",
    options: [
      { text: "Grounded and loving—boundaries enhance mutual respect.", points: 4 },
      { text: "A bit hesitant at times, but I follow through for mutual well-being.", points: 3 },
      { text: "Guilty, as if holding a boundary is selfish or hurtful to my partner.", points: 2 },
      { text: "Terrified that standing up for myself will destroy the relationship entirely.", points: 1 }
    ]
  },
  {
    id: 5,
    category: "Trust & Connection",
    question: "When your partner needs space or time away, how does your heart react?",
    options: [
      { text: "I feel completely secure and cherish my own independent joy.", points: 4 },
      { text: "I notice a slight flutter of unease, but quickly reassure myself.", points: 3 },
      { text: "I overthink their motives and mentally replay recent conversations.", points: 2 },
      { text: "I panic or suspect they are pulling away permanently.", points: 1 }
    ]
  },
  {
    id: 6,
    category: "Conflict Resolution",
    question: "After an intense argument, how is repair typically achieved?",
    options: [
      { text: "Through empathetic listening, sincere apologies, and a warm embrace.", points: 4 },
      { text: "We take a cooling period and then talk through what happened calmly.", points: 3 },
      { text: "Things sweep under the rug without true emotional resolution.", points: 2 },
      { text: "Cold shoulders, prolonged distance, or explosive unresolved cycles continue.", points: 1 }
    ]
  },
  {
    id: 7,
    category: "Intimacy & Presence",
    question: "During intimate or quiet moments together, where is your mental attention?",
    options: [
      { text: "100% present, bathed in warmth and grateful connection.", points: 4 },
      { text: "Mostly present, though occasional daily anxieties float in.", points: 3 },
      { text: "Distracted by worries about where the relationship is heading long-term.", points: 2 },
      { text: "Guarded or emotionally detached, unable to let my guard down fully.", points: 1 }
    ]
  },
  {
    id: 8,
    category: "Emotional Energy",
    question: "How energizing or draining does navigating your relationship feel currently?",
    options: [
      { text: "A sanctuary of renewal, peace, and mutual upliftment.", points: 4 },
      { text: "Balanced with healthy highs and occasional working moments.", points: 3 },
      { text: "Noticeably tiring—I spend significant mental energy managing moods.", points: 2 },
      { text: "Exhausting. I feel like I am walking on eggshells daily.", points: 1 }
    ]
  },
  {
    id: 9,
    category: "Forgiveness & Compassion",
    question: "When your partner makes a mistake or drops the ball, how easily do you extend grace?",
    options: [
      { text: "With genuine compassion and open dialogue.", points: 4 },
      { text: "It takes a moment to breathe through, but I extend grace readily.", points: 3 },
      { text: "I forgive outwardly, but silently keep score inside.", points: 2 },
      { text: "I hold onto deep frustration, feeling repeated disappointment.", points: 1 }
    ]
  },
  {
    id: 10,
    category: "Vision for Healing",
    question: "When you look into the future of your heart and love life, what is your core emotion?",
    options: [
      { text: "Profound hope, deep security, and flourishing love.", points: 4 },
      { text: "Optimism paired with a desire to strengthen our foundation.", points: 3 },
      { text: "Uncertainty—I want to heal but don't know the exact steps.", points: 2 },
      { text: "Longing for a breakthrough because the pain feels too heavy to carry alone.", points: 1 }
    ]
  }
];

// Outcomes exactly from design guidelines with updated scoring brackets & product routing
const OUTCOME_BRACKETS = [
  {
    score_range: "34 - 40",
    level: 1,
    title: "Strong Relationship Foundation",
    subtitle: "Radiant, Secure & Rooted in Deep Mutual Harmony",
    color_theme: "emerald",
    badge: "Secure Foundation",
    summary: "You appear to have a relatively healthy relationship foundation and are primarily looking to strengthen communication, emotional intimacy, conscious partnership, and relationship skills.",
    primary_product: "Relationship Skill Blueprint",
    product_desc: "Build stronger communication, emotional intimacy, conflict skills, and conscious relationship habits.",
    best_next_step: "Your relationship has a meaningful foundation. Your next level is developing stronger relationship skills, communication, emotional awareness, and conscious partnership.",
    tagmango_link: "https://pretty.heartglowhub.net/v2/auth/sign-up"
  },
  {
    score_range: "27 - 33",
    level: 2,
    title: "Relationship Growth",
    subtitle: "Awakening, Growing & Building Sacred Alignment",
    color_theme: "amber",
    badge: "Relational Growth",
    summary: "You have a relationship foundation but have identifiable patterns that could be improved through greater understanding and relationship skills.",
    primary_product: "Relationship Skill Blueprint",
    product_desc: "Build stronger communication, emotional intimacy, conflict skills, and conscious relationship habits.",
    best_next_step: "Your relationship can benefit from deeper understanding of relationship patterns and how partners may communicate, connect, and respond differently.",
    tagmango_link: "https://pretty.heartglowhub.net/v2/auth/sign-up"
  },
  {
    score_range: "20 - 26",
    level: 3,
    title: "Emotional Healing / Relationship Strain",
    subtitle: "Seeking Clarity, Repair & Emotional Unburdening",
    color_theme: "rose",
    badge: "Healing Zone",
    summary: "Your responses suggest that relationship difficulties may be connected to deeper emotional patterns, boundaries, attachment patterns, emotional regulation, or unresolved wounds.",
    primary_product: "The Relationship Renaissance — SILVER",
    product_desc: "Start healing the emotional patterns, boundaries, attachment dynamics, and body-held tension that may be influencing your relationship.",
    best_next_step: "Your next step may not be trying harder in the relationship. It may be understanding and healing the patterns that are influencing how you connect, communicate, and protect yourself.",
    tagmango_link: "https://pretty.heartglowhub.net/v2/auth/sign-up"
  },
  {
    score_range: "10 - 19",
    level: 4,
    title: "Deeper Emotional Healing",
    subtitle: "Longing for a Sacred Breakthrough & Total Renewal",
    color_theme: "purple",
    badge: "Deeper Healing Zone",
    summary: "Your responses suggest a stronger need for deeper emotional healing and processing of unresolved relationship experiences.",
    primary_product: "The Relationship Renaissance — GOLD",
    product_desc: "Go deeper into inner-child healing, shadow work, grief processing, and releasing unresolved emotional ties from the past.",
    best_next_step: "Your responses suggest that some of your relationship patterns may be connected to deeper unresolved emotional experiences. A deeper healing pathway may help you understand what is underneath the pattern rather than repeatedly reacting to it.",
    tagmango_link: "https://pretty.heartglowhub.net/v2/auth/sign-up"
  }
];

export default function App() {
  // State management for custom static funnel
  const [quizState, setQuizState] = useState(() => {
    return localStorage.getItem("hgh_quiz_state") || "landing";
  });
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(() => {
    const saved = localStorage.getItem("hgh_current_index");
    return saved ? parseInt(saved, 10) : 0;
  });
  const [answers, setAnswers] = useState(() => {
    const saved = localStorage.getItem("hgh_answers");
    return saved ? JSON.parse(saved) : Array(10).fill(null);
  });
  const [leadName, setLeadName] = useState(() => {
    return localStorage.getItem("hgh_lead_name") || "";
  });
  const [leadEmail, setLeadEmail] = useState(() => {
    return localStorage.getItem("hgh_lead_email") || "";
  });
  const [partnerGender, setPartnerGender] = useState(() => {
    return localStorage.getItem("hgh_partner_gender") || "man";
  });
  const [selectedOption, setSelectedOption] = useState(null);
  const [faqOpenIndex, setFaqOpenIndex] = useState(null);

  // Somatic Timer States
  const [timerActive, setTimerActive] = useState(false);
  const [timeLeft, setTimeLeft] = useState(60);
  const [breathPhase, setBreathPhase] = useState("Inhale");

  // WhatsApp Delivery States
  const [whatsAppDeliveryActive, setWhatsAppDeliveryActive] = useState(false);
  const [whatsAppNumber, setWhatsAppNumber] = useState(() => {
    return localStorage.getItem("hgh_whatsapp_number") || "";
  });
  const [whatsAppConsent, setWhatsAppConsent] = useState(() => {
    return localStorage.getItem("hgh_whatsapp_consent") === "true";
  });
  const [whatsAppSubmitted, setWhatsAppSubmitted] = useState(false);

  // Couple Comparison States
  const [partnerCommScore, setPartnerCommScore] = useState(3);
  const [partnerTriggerScore, setPartnerTriggerScore] = useState(3);
  const [partnerIntimacyScore, setPartnerIntimacyScore] = useState(3);
  const [showComparison, setShowComparison] = useState(false);

  // Meditation Audio States
  const [meditationPlaying, setMeditationPlaying] = useState(false);
  const [meditationProgress, setMeditationProgress] = useState(0);
  const [meditationTime, setMeditationTime] = useState("0:00");
  const [meditationDuration, setMeditationDuration] = useState("6:10");
  const audioRef = useRef(null);

  const handleLoadedMetadata = (e) => {
    const audio = e.currentTarget;
    if (audio && audio.duration) {
      const min = Math.floor(audio.duration / 60);
      const sec = Math.floor(audio.duration % 60);
      setMeditationDuration(`${min}:${sec < 10 ? "0" : ""}${sec}`);
    }
  };

  const handleTimeUpdate = (e) => {
    const audio = e.currentTarget;
    if (audio && audio.duration) {
      setMeditationProgress((audio.currentTime / audio.duration) * 100);
      // Format time
      const min = Math.floor(audio.currentTime / 60);
      const sec = Math.floor(audio.currentTime % 60);
      setMeditationTime(`${min}:${sec < 10 ? "0" : ""}${sec}`);
    }
  };

  const handleEnded = () => {
    setMeditationPlaying(false);
    setMeditationProgress(0);
    setMeditationTime("0:00");
  };

  const toggleMeditationPlay = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (meditationPlaying) {
      audio.pause();
      setMeditationPlaying(false);
      toast.info("Meditation paused.");
    } else {
      audio.play().then(() => {
        setMeditationPlaying(true);
        toast.success("Playing your free guided somatic meditation. Close your eyes and breathe.");
      }).catch((err) => {
        console.warn("Audio play blocked or failed:", err);
        // fallback to mock play state so they can still see progress
        setMeditationPlaying(true);
      });
    }
  };

  // Somatic breathing timer effect
  useEffect(() => {
    let interval = null;
    if (timerActive) {
      interval = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            setTimerActive(false);
            setBreathPhase("Completed");
            toast.success("Somatic centering complete. Your nervous system is now primed for relational clarity.");
            return 0;
          }
          const nextTime = prev - 1;
          // Box breathing phase calculation (4s Inhale, 4s Hold, 4s Exhale)
          const cycleSec = (60 - nextTime) % 12;
          if (cycleSec < 4) {
            setBreathPhase("Inhale");
          } else if (cycleSec < 8) {
            setBreathPhase("Hold");
          } else {
            setBreathPhase("Exhale");
          }
          return nextTime;
        });
      }, 1000);
    } else {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [timerActive]);

  // Sync state to local storage to persist user inputs
  useEffect(() => {
    localStorage.setItem("hgh_quiz_state", quizState);
    localStorage.setItem("hgh_current_index", currentQuestionIndex.toString());
    localStorage.setItem("hgh_answers", JSON.stringify(answers));
    localStorage.setItem("hgh_lead_name", leadName);
    localStorage.setItem("hgh_lead_email", leadEmail);
    localStorage.setItem("hgh_whatsapp_number", whatsAppNumber);
    localStorage.setItem("hgh_whatsapp_consent", whatsAppConsent ? "true" : "false");
    localStorage.setItem("hgh_partner_gender", partnerGender);
  }, [quizState, currentQuestionIndex, answers, leadName, leadEmail, whatsAppNumber, whatsAppConsent, partnerGender]);

  // Calculate score & outcome
  const calculateTotalScore = () => {
    return answers.reduce((sum, val) => sum + (val !== null ? val : 0), 0);
  };

  const getOutcomeBracket = (score) => {
    if (score >= 34) return OUTCOME_BRACKETS[0];
    if (score >= 27) return OUTCOME_BRACKETS[1];
    if (score >= 20) return OUTCOME_BRACKETS[2];
    return OUTCOME_BRACKETS[3];
  };

  const currentScore = calculateTotalScore();
  const currentOutcome = getOutcomeBracket(currentScore);

  // Dynamic category calculations based on existing structure
  const getDynamicInsights = () => {
    const categories = [
      { name: "Emotional Trigger Reaction", desc: "How you handle internal emotional spikes during disagreements." },
      { name: "Communication Flow", desc: "Your level of comfort and safety in sharing vulnerable needs directly." },
      { name: "Past Hurt & Resentment", desc: "How effectively you process and let go of previous relationship residue." },
      { name: "Self-Worth & Boundaries", desc: "Your capacity to set healthy boundaries without guilt or fear." },
      { name: "Trust & Connection", desc: "Your sense of safety and security when partners request space or time." },
      { name: "Conflict Resolution", desc: "The methods and speed with which you achieve repair after arguments." },
      { name: "Intimacy & Presence", desc: "Your ability to be emotionally present in quiet or close moments." },
      { name: "Emotional Energy", desc: "The balance of vitality versus exhaustion you experience in relationship dynamics." },
      { name: "Forgiveness & Compassion", desc: "Your capacity to extend grace versus keeping score silently." },
      { name: "Vision for Healing", desc: "Your level of clarity, optimism, and concrete steps toward the future of love." }
    ];

    const mapped = categories.map((cat, idx) => ({
      ...cat,
      score: answers[idx] !== null ? answers[idx] : 2
    }));

    let strongest = mapped[0];
    let growth = mapped[0];

    mapped.forEach(item => {
      if (item.score > strongest.score) {
        strongest = item;
      }
      if (item.score < growth.score) {
        growth = item;
      }
    });

    if (strongest.name === growth.name) {
      const otherStrong = mapped.find(item => item.name !== strongest.name && item.score >= strongest.score);
      if (otherStrong) strongest = otherStrong;
      const otherGrowth = mapped.find(item => item.name !== growth.name && item.score <= growth.score);
      if (otherGrowth) growth = otherGrowth;
    }

    return { strongest, growth };
  };

  const { strongest: strengthArea, growth: growthArea } = getDynamicInsights();
  const resiliencePercent = Math.round((currentScore / 40) * 100);

  const fallbackCopy = (text) => {
    try {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.top = "0";
      textArea.style.left = "0";
      textArea.style.position = "fixed";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const successful = document.execCommand("copy");
      document.body.removeChild(textArea);
      if (successful) {
        toast.success("Aesthetic sharing blueprint copied to clipboard. Paste anywhere to invite partner growth!");
      } else {
        toast.error("Copying failed. Please copy the link manually.");
      }
    } catch (err) {
      console.error("Fallback copy failed:", err);
      toast.error("Copying failed. Please copy the link manually.");
    }
  };

  // Pattern interpretation sentences
  const getRelationshipPatternInfo = () => {
    if (currentScore >= 34) {
      return {
        pattern: "Conscious & Secure Alliance",
        whatThisMeans: [
          "You appear to have a healthy relationship foundation and are primarily looking to strengthen communication, emotional intimacy, conscious partnership, and relationship skills.",
          "During disagreements, you generally maintain a calm internal posture and seek constructive repair without getting defensive.",
          "Your responses suggest a high capacity for emotional presence, trust, and healthy mutual boundaries.",
          "Your strongest opportunity lies in adopting advanced relational skillsets to elevate your connection to deeper sacred intimacy."
        ]
      };
    } else if (currentScore >= 27) {
      return {
        pattern: "Transitioning & Developing Connection",
        whatThisMeans: [
          "You possess wonderful self-awareness and a warm desire for growth, but have identifiable patterns that can be improved.",
          "While old communication blocks occasionally surface, you actively catch yourself and seek loving repair with your partner.",
          "Understanding how masculine and feminine partner perspectives operate differently is your highest leverage path.",
          "Developing consistent communication habits will allow you to dissolve repeating tension before it creates distance."
        ]
      };
    } else if (currentScore >= 20) {
      return {
        pattern: "Protected & Strained Connection",
        whatThisMeans: [
          "Your responses suggest that relationship difficulties may be connected to deeper emotional patterns, boundaries, attachment styles, or body-held tension.",
          "When feeling unsafe or trigger-activated, your current patterns can cause you to retreat into silence or suppress authentic needs.",
          "Your next step may not be trying harder in the relationship. It may be understanding and healing the patterns that are influencing how you connect, communicate, and protect yourself.",
          "Strengthening boundaries and releasing emotional tension held in the body will help you relate from a place of secure self-alignment."
        ]
      };
    } else {
      return {
        pattern: "Deeper Emotional Processing",
        whatThisMeans: [
          "Your responses suggest that some of your relationship patterns may be connected to deeper unresolved emotional experiences from past relationships or early life.",
          "A deeper healing pathway may help you understand what is underneath the pattern rather than repeatedly reacting to it.",
          "You are carrying heavy emotional shields, which can leave you feeling exhausted or walking on eggshells in daily interactions.",
          "Prioritizing deep inner-child work, shadow processing, and energetic unburdening is essential to unlock authentic connection."
        ]
      };
    }
  };

  const patternInfo = getRelationshipPatternInfo();

  // Dynamic Product recommendation language & checkouts
  const getDynamicProductInfo = () => {
    // 1. Foundation is ALWAYS Relationship Skill Blueprint for everyone
    const foundation = {
      title: "Relationship Skill Blueprint",
      desc: "Build stronger communication, emotional intimacy, conflict skills, and conscious relationship habits.",
      position: "Builds the skills to communicate, connect and navigate the relationship more consciously.",
      checkoutUrl: "https://pretty.heartglowhub.net/v2/auth/sign-up?product=skill_blueprint"
    };

    // 2. Specialized next path (secondary)
    let secondary = null;
    let positioning = "";

    if (currentScore >= 34) {
      // Level 1
      if (partnerGender === "man") {
        secondary = {
          title: "Decode Man",
          desc: "Understand male emotional and relationship patterns more deeply so you can communicate and connect with greater clarity.",
          checkoutUrl: "https://pretty.heartglowhub.net/v2/auth/sign-up?product=decode_man"
        };
        positioning = "Relationship Skill Blueprint gives you the core tools to communicate consciously, while Decode Man unlocks deep insights into male relational behavior to dissolve connection gaps.";
      } else if (partnerGender === "woman") {
        secondary = {
          title: "Decode Woman",
          desc: "Understand female emotional and relationship patterns more deeply so you can communicate and connect with greater clarity.",
          checkoutUrl: "https://pretty.heartglowhub.net/v2/auth/sign-up?product=decode_woman"
        };
        positioning = "Relationship Skill Blueprint gives you the core tools to communicate consciously, while Decode Woman helps you understand female emotional expression to meet your partner with presence.";
      } else {
        secondary = {
          title: "Love Bundle (Decode Man + Decode Woman)",
          desc: "Understand both sides of the relationship dynamic with Decode Man + Decode Woman.",
          checkoutUrl: "https://pretty.heartglowhub.net/v2/auth/sign-up?product=love_bundle"
        };
        positioning = "Relationship Skill Blueprint establishes your core relationship skills, while the Love Bundle offers profound perspectives on both masculine and feminine communication styles.";
      }
    } else if (currentScore >= 27) {
      // Level 2
      if (partnerGender === "man") {
        secondary = {
          title: "Decode Man",
          desc: "Understand male emotional and relationship patterns more deeply so you can communicate and connect with greater clarity.",
          checkoutUrl: "https://pretty.heartglowhub.net/v2/auth/sign-up?product=decode_man"
        };
        positioning = "Relationship Skill Blueprint helps you build the skills to communicate more consciously, while Decode Man allows you to better understand male relational responses.";
      } else if (partnerGender === "woman") {
        secondary = {
          title: "Decode Woman",
          desc: "Understand female emotional and relationship patterns more deeply so you can communicate and connect with greater clarity.",
          checkoutUrl: "https://pretty.heartglowhub.net/v2/auth/sign-up?product=decode_woman"
        };
        positioning = "Relationship Skill Blueprint helps you build the skills to communicate more consciously, while Decode Woman allows you to better understand female emotional processing.";
      } else {
        secondary = {
          title: "Love Bundle (Decode Man + Decode Woman)",
          desc: "Understand both sides of the relationship dynamic with Decode Man + Decode Woman.",
          checkoutUrl: "https://pretty.heartglowhub.net/v2/auth/sign-up?product=love_bundle"
        };
        positioning = "Relationship Skill Blueprint helps you build the skills to communicate more consciously, while the Love Bundle helps you decode how both partners communicate differently.";
      }
    } else if (currentScore >= 20) {
      // Level 3
      secondary = {
        title: "The Relationship Renaissance — SILVER",
        desc: "Start healing the emotional patterns, boundaries, attachment dynamics, and body-held tension that may be influencing your relationship.",
        checkoutUrl: "https://pretty.heartglowhub.net/v2/auth/sign-up?product=trr_silver"
      };
      positioning = "Relationship Skill Blueprint helps you build the skills to communicate, connect and navigate the relationship more consciously, while Relationship Renaissance — Silver helps you work on the deeper emotional patterns underneath those challenges.";
    } else {
      // Level 4
      secondary = {
        title: "The Relationship Renaissance — GOLD",
        desc: "Go deeper into inner-child healing, shadow work, grief processing, and releasing unresolved emotional ties from the past.",
        checkoutUrl: "https://pretty.heartglowhub.net/v2/auth/sign-up?product=trr_gold"
      };
      positioning = "Relationship Skill Blueprint gives you the practical relationship skills, while Relationship Renaissance — Gold supports deeper inner-child, shadow, grief and unresolved emotional healing work.";
    }

    return { foundation, secondary, positioning };
  };

  const productInfo = getDynamicProductInfo();

  // Handlers
  const startQuiz = () => {
    setAnswers(Array(10).fill(null));
    setCurrentQuestionIndex(0);
    setSelectedOption(null);
    setQuizState("quiz");
    toast.success("Relationship Healing assessment started. Take your time with each question.");
    
    // Smooth scroll to quiz card
    setTimeout(() => {
      const el = document.getElementById("quiz-anchor");
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  const selectOptionHandler = (points, idx) => {
    setSelectedOption(idx);
    
    // Auto-advance after small feedback delay
    setTimeout(() => {
      const newAnswers = [...answers];
      newAnswers[currentQuestionIndex] = points;
      setAnswers(newAnswers);

      if (currentQuestionIndex < 9) {
        setCurrentQuestionIndex(currentQuestionIndex + 1);
        setSelectedOption(null);
      } else {
        setQuizState("lead");
      }
    }, 350);
  };

  const handlePrevQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
      setSelectedOption(answers[currentQuestionIndex - 1]);
    }
  };

  const submitLeadForm = (e) => {
    e.preventDefault();
    if (!leadName.trim()) {
      toast.error("Please enter your name to unlock your personalized results.");
      return;
    }
    if (!leadEmail.trim() || !leadEmail.includes("@")) {
      toast.error("Please enter a valid email address.");
      return;
    }
    if (!whatsAppNumber.trim()) {
      toast.error("Please enter your WhatsApp number to receive your free meditation.");
      return;
    }
    if (!whatsAppConsent) {
      toast.error("Please agree to receive updates and meditation on WhatsApp to continue.");
      return;
    }

    setQuizState("results");
    toast.success(`Success! Your healing blueprint has been generated, ${leadName}.`);
    
    // Scroll to results top
    setTimeout(() => {
      const el = document.getElementById("quiz-anchor");
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  const resetQuiz = () => {
    setAnswers(Array(10).fill(null));
    setCurrentQuestionIndex(0);
    setLeadName("");
    setLeadEmail("");
    setWhatsAppNumber("");
    setWhatsAppConsent(false);
    setSelectedOption(null);
    setQuizState("landing");
    localStorage.removeItem("hgh_quiz_state");
    localStorage.removeItem("hgh_current_index");
    localStorage.removeItem("hgh_answers");
    localStorage.removeItem("hgh_lead_name");
    localStorage.removeItem("hgh_lead_email");
    localStorage.removeItem("hgh_whatsapp_number");
    localStorage.removeItem("hgh_whatsapp_consent");
    toast.info("Assessment reset successfully.");
  };

  const handlePrint = () => {
    window.print();
  };

  const toggleFaq = (index) => {
    setFaqOpenIndex(faqOpenIndex === index ? null : index);
  };

  // Determine outcome color classes for progress, tags & backgrounds
  const getThemeClasses = (theme) => {
    switch (theme) {
      case "emerald":
        return {
          badge: "bg-emerald-50 text-emerald-800 border-emerald-200",
          text: "text-emerald-700",
          progress: "bg-emerald-600",
          scoreBg: "bg-emerald-50 border-emerald-100 text-emerald-900",
          ring: "ring-emerald-200"
        };
      case "amber":
        return {
          badge: "bg-amber-50 text-amber-800 border-amber-200",
          text: "text-amber-700",
          progress: "bg-amber-600",
          scoreBg: "bg-amber-50 border-amber-100 text-amber-900",
          ring: "ring-amber-200"
        };
      case "rose":
        return {
          badge: "bg-rose-50 text-rose-800 border-rose-200",
          text: "text-rose-700",
          progress: "bg-rose-600",
          scoreBg: "bg-rose-50 border-rose-100 text-rose-900",
          ring: "ring-rose-200"
        };
      case "purple":
        default:
        return {
          badge: "bg-purple-50 text-purple-800 border-purple-200",
          text: "text-purple-700",
          progress: "bg-purple-600",
          scoreBg: "bg-purple-50 border-purple-100 text-purple-900",
          ring: "ring-purple-200"
        };
    }
  };

  const themeClasses = getThemeClasses(currentOutcome.color_theme);

  // Dynamic TagMango checkout URL
  const checkoutUrl = `${currentOutcome.tagmango_link}?score=${currentScore}&bracket=${encodeURIComponent(currentOutcome.title)}&name=${encodeURIComponent(leadName)}&email=${encodeURIComponent(leadEmail)}`;

  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-50/70 via-amber-50/40 to-stone-100/90 text-slate-800 antialiased selection:bg-rose-100 print:bg-white print:text-black">
      <Toaster position="top-right" richColors />

      {/* Sticky Header & Brand Bar */}
      <header className="sticky top-0 z-50 bg-white/70 backdrop-blur-md border-b border-rose-100/40 transition-all duration-200 print:hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-full bg-rose-600 flex items-center justify-center shadow-md shadow-rose-600/10">
              <Heart className="w-5 h-5 text-white animate-pulse" />
            </div>
            <div>
              <span className="font-serif text-xl font-bold tracking-tight text-slate-900">Heart Glow Hub</span>
              <span className="block text-[10px] font-sans text-slate-500 uppercase tracking-widest font-semibold">Mango AI Partner</span>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <span className="hidden md:inline-flex items-center text-xs font-sans text-slate-500 bg-white/80 px-3 h-8 rounded-full border border-slate-200/60 shadow-sm font-medium">
              <Shield className="w-3.5 h-3.5 text-emerald-600 mr-1.5" />
              Your responses are private and handled with care.
            </span>
            {quizState !== "landing" && (
              <button
                onClick={resetQuiz}
                data-testid="header-restart-btn"
                className="text-xs font-sans font-semibold text-rose-700 hover:text-rose-800 transition-colors flex items-center gap-1 bg-rose-50 hover:bg-rose-100/80 px-3 h-8 rounded-lg"
              >
                <RefreshCw className="w-3 h-3" />
                Start Over
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
        
        {/* LANDING STATE */}
        {quizState === "landing" && (
          <div className="space-y-24">
            {/* Hero Section */}
            <section className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
              <div className="lg:col-span-7 space-y-8 text-left">
                <div className="space-y-4">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-sans font-medium bg-rose-50 border border-rose-100 text-rose-700 tracking-wide uppercase">
                    <Sparkles className="w-3 h-3 mr-1 text-rose-500" />
                    Deep Relational Psychology
                  </span>
                  <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold tracking-tight text-slate-900 leading-[1.15]">
                    Discover Where Your <br/>
                    <span className="text-rose-700 underline decoration-rose-300/60 decoration-wavy underline-offset-4">Relationship Stands</span> <br/>
                    In Exactly 3 Minutes.
                  </h1>
                </div>

                <p className="text-base sm:text-lg lg:text-xl font-sans text-slate-600 leading-relaxed max-w-2xl">
                  Take a compassionate 10-question relationship assessment designed to help you uncover communication patterns, emotional triggers, connection gaps and your personalized pathway toward healthier, more fulfilling love.
                </p>

                <div className="flex flex-col sm:flex-row gap-4 pt-2">
                  <button
                    onClick={startQuiz}
                    data-testid="quiz-start-button"
                    className="inline-flex items-center justify-center font-sans font-semibold text-base tracking-wide h-14 px-8 rounded-xl bg-rose-700 hover:bg-rose-800 text-white shadow-xl shadow-rose-700/20 transition-all duration-300 hover:translate-y-[-2px] group"
                  >
                    Start Your Free Healing Quiz
                    <ArrowRight className="w-5 h-5 ml-2 transition-transform duration-200 group-hover:translate-x-1" />
                  </button>
                  <a
                    href="#mentor-section"
                    className="inline-flex items-center justify-center font-sans font-semibold text-slate-700 hover:text-slate-950 bg-white hover:bg-slate-50 border border-slate-200/80 rounded-xl h-14 px-8 shadow-sm transition-all duration-200"
                  >
                    Meet Pallavi Nandedkar
                  </a>
                </div>

                {/* Trust Badges */}
                <div className="pt-6 border-t border-rose-100/50 grid grid-cols-3 gap-4">
                  <div className="text-left">
                    <span className="block font-serif text-2xl font-bold text-slate-900">15,000+</span>
                    <span className="text-xs text-slate-500 font-sans">Hearts Guided</span>
                  </div>
                  <div className="text-left">
                    <span className="block font-serif text-2xl font-bold text-slate-900">100%</span>
                    <span className="text-xs text-slate-500 font-sans">Confidential & Private</span>
                  </div>
                  <div className="text-left">
                    <span className="block font-serif text-lg sm:text-2xl font-bold text-slate-900 leading-tight">Relationship Expertise</span>
                    <span className="text-xs text-slate-500 font-sans">Practical & Compassionate</span>
                  </div>
                </div>
              </div>

              {/* Asymmetric Elegant Portrait Grid / Frame */}
              <div className="lg:col-span-5 relative">
                <div className="absolute inset-0 bg-gradient-to-tr from-amber-200/20 via-rose-300/20 to-stone-100/10 rounded-3xl blur-3xl -z-10" />
                <div className="relative rounded-2xl overflow-hidden border-4 border-white shadow-2xl shadow-rose-950/5">
                  <img
                    src="https://images.unsplash.com/photo-1494774157365-9e04c6720e47?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHw0fHxjb3VwbGVzJTIwcXVpZXQlMjBtb21lbnQlMjBoZWFsaW5nJTIwd2FybXRoJTIwc3Vuc2V0JTIwZW1icmFjZXxlbnwwfHx8fDE3ODkwNDIyMjl8MA&ixlib=rb-4.1.0&q=85"
                    alt="Couple enjoying quiet intimate moment during sunset, representing healthy relationships and emotional safety"
                    className="w-full h-[450px] object-cover hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-slate-950/80 via-slate-950/30 to-transparent p-6 text-left">
                    <p className="text-white font-serif text-lg italic">
                      "Repair is not the absence of struggle. It is the brave decision to turn towards each other."
                    </p>
                    <span className="block text-rose-200 font-sans text-xs mt-2 font-medium">Pallavi Nandedkar, Relationship Expert & Mindset Coach</span>
                  </div>
                </div>
              </div>
            </section>

            {/* Quote banner */}
            <section className="bg-white/60 border border-rose-100/40 rounded-3xl p-8 sm:p-12 text-left relative overflow-hidden backdrop-blur-sm">
              <div className="absolute right-0 top-0 translate-x-12 -translate-y-12 text-rose-100 opacity-20 select-none">
                <Heart className="w-72 h-72" />
              </div>
              <div className="relative max-w-4xl space-y-6">
                <h2 className="text-2xl sm:text-3xl font-serif font-semibold text-slate-900 tracking-tight">
                  Why Understanding Your Heart's Blueprint is Non-Negotiable
                </h2>
                <p className="text-slate-600 font-sans text-base leading-relaxed">
                  Most relational difficulties stem from outdated blueprints—unconscious defense mechanisms built to withstand past wounds. By mapping your responses to emotional friction, connection loss, and boundaries, you uncover the specific levers that can shift frustration into safe, reciprocal, and profound adoration.
                </p>
                <div className="flex flex-col sm:flex-row gap-6 text-slate-700 text-sm font-sans pt-4 border-t border-rose-100/50">
                  <span className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                    Recognize somatic triggers instantly
                  </span>
                  <span className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                    Understand your defense architecture
                  </span>
                  <span className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                    Adopt custom conflict-repair scripts
                  </span>
                </div>
              </div>
            </section>
          </div>
        )}

        {/* QUIZ ANCHOR ELEMENT */}
        <div id="quiz-anchor" className="scroll-mt-24" />

        {/* QUIZ INTERACTIVE CONTAINER */}
        {quizState === "quiz" && (
          <div className="max-w-3xl mx-auto space-y-8 text-left">
            {/* Header/Progress Indicator */}
            <div className="space-y-4">
              <div className="flex items-center justify-between text-sm font-sans">
                <span className="font-semibold text-rose-700 tracking-wider uppercase text-xs">
                  Category: {QUESTIONS_DATA[currentQuestionIndex].category}
                </span>
                <span className="text-slate-500 font-medium">
                  Question {currentQuestionIndex + 1} of 10
                </span>
              </div>

              {/* Custom styled progress bar */}
              <div className="h-2 w-full bg-slate-200/70 rounded-full overflow-hidden shadow-inner">
                <div
                  className="h-full bg-gradient-to-r from-rose-500 via-rose-600 to-amber-500 transition-all duration-300"
                  style={{ width: `${((currentQuestionIndex + 1) / 10) * 100}%` }}
                />
              </div>
            </div>

            {/* Main Question Card */}
            <div className="bg-white/80 backdrop-blur-md border border-rose-100/60 shadow-xl shadow-rose-900/5 rounded-3xl p-6 sm:p-10 space-y-8">
              <h2 className="text-xl sm:text-2xl lg:text-3xl font-serif font-medium text-slate-800 leading-snug">
                {QUESTIONS_DATA[currentQuestionIndex].question}
              </h2>

              <div className="space-y-4">
                {QUESTIONS_DATA[currentQuestionIndex].options.map((option, idx) => {
                  const isSelected = selectedOption === idx;
                  return (
                    <button
                      key={idx}
                      onClick={() => selectOptionHandler(option.points, idx)}
                      data-testid={`quiz-question-option-${idx}`}
                      className={`w-full text-left p-5 rounded-2xl border text-sm sm:text-base font-sans transition-all duration-200 flex justify-between items-center group cursor-pointer outline-none focus:ring-2 focus:ring-rose-300/50 ${
                        isSelected
                          ? "bg-rose-50/95 border-rose-400 text-rose-950 shadow-md ring-2 ring-rose-300/50"
                          : "bg-white/90 hover:bg-rose-50/40 border-slate-200/80 hover:border-rose-300 text-slate-800 transition-all duration-200 shadow-sm"
                      }`}
                    >
                      <span className="pr-4 leading-relaxed font-medium">{option.text}</span>
                      <div className={`w-5 h-5 rounded-full border flex-shrink-0 flex items-center justify-center transition-all ${
                        isSelected
                          ? "bg-rose-600 border-rose-600 text-white"
                          : "border-slate-300 group-hover:border-rose-400 bg-white"
                      }`}>
                        {isSelected && <Check className="w-3.5 h-3.5" />}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Navigation Back Button */}
              {currentQuestionIndex > 0 && (
                <button
                  onClick={handlePrevQuestion}
                  data-testid="quiz-back-button"
                  className="inline-flex items-center text-sm font-sans font-semibold text-slate-500 hover:text-slate-800 transition-colors py-2 group cursor-pointer"
                >
                  <ArrowLeft className="w-4 h-4 mr-2 transition-transform group-hover:translate-x-[-2px]" />
                  Previous Question
                </button>
              )}
            </div>

            {/* Quick reminder text */}
            <p className="text-center text-xs font-sans text-slate-400 flex items-center justify-center gap-1.5">
              <Shield className="w-3.5 h-3.5 text-emerald-600/70" />
              Your answers are securely cached locally. Refreshing won't lose your progress.
            </p>
          </div>
        )}

        {/* MANDATORY LEAD CAPTURE FORM */}
        {quizState === "lead" && (
          <div className="max-w-xl mx-auto text-left">
            <div className="bg-white/90 backdrop-blur-md border border-rose-100/60 shadow-xl shadow-rose-900/5 rounded-3xl p-8 sm:p-12 space-y-8 relative overflow-hidden">
              <div className="absolute -right-8 -top-8 w-24 h-24 bg-rose-50 rounded-full blur-2xl" />

              <div className="space-y-4">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-sans font-medium bg-emerald-50 border border-emerald-100 text-emerald-800">
                  <Lock className="w-3 h-3 mr-1" />
                  Assessment Complete
                </span>
                <h2 className="text-2xl sm:text-3xl font-serif font-semibold text-slate-900 tracking-tight">
                  Unlock Your Sacred Healing Blueprint
                </h2>
                <p className="text-slate-600 font-sans text-sm sm:text-base leading-relaxed">
                  We’ve mapped your responses to reveal your key relationship growth areas and your personalized path forward.
                </p>
              </div>

              <form onSubmit={submitLeadForm} className="space-y-5">
                <div className="space-y-2">
                  <label className="block text-xs font-sans font-semibold text-slate-700 uppercase tracking-wider text-left">
                    Your First Name
                  </label>
                  <div className="relative">
                    <User className="absolute left-4 top-3.5 w-5 h-5 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Jane"
                      value={leadName}
                      onChange={(e) => setLeadName(e.target.value)}
                      data-testid="lead-name-input"
                      className="w-full font-sans pl-12 pr-4 py-3.5 rounded-xl border border-slate-200 bg-white/80 focus:outline-none focus:ring-2 focus:ring-rose-400 focus:border-rose-400 transition-all shadow-sm text-sm"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-xs font-sans font-semibold text-slate-700 uppercase tracking-wider text-left">
                    Your Primary Email
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-3.5 w-5 h-5 text-slate-400" />
                    <input
                      type="email"
                      placeholder="jane@heartglow.com"
                      value={leadEmail}
                      onChange={(e) => setLeadEmail(e.target.value)}
                      data-testid="lead-email-input"
                      className="w-full font-sans pl-12 pr-4 py-3.5 rounded-xl border border-slate-200 bg-white/80 focus:outline-none focus:ring-2 focus:ring-rose-400 focus:border-rose-400 transition-all shadow-sm text-sm"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-xs font-sans font-semibold text-slate-700 uppercase tracking-wider text-left">
                    Your WhatsApp Number
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-4 top-3.5 w-5 h-5 text-slate-400" />
                    <input
                      type="tel"
                      placeholder="+91 XXXXX XXXXX"
                      value={whatsAppNumber}
                      onChange={(e) => setWhatsAppNumber(e.target.value)}
                      data-testid="lead-whatsapp-input"
                      className="w-full font-sans pl-12 pr-4 py-3.5 rounded-xl border border-slate-200 bg-white/80 focus:outline-none focus:ring-2 focus:ring-rose-400 focus:border-rose-400 transition-all shadow-sm text-sm"
                      required
                    />
                  </div>
                </div>

                <div className="flex items-start gap-2.5 pt-1">
                  <input
                    type="checkbox"
                    id="whatsapp-consent"
                    checked={whatsAppConsent}
                    onChange={(e) => setWhatsAppConsent(e.target.checked)}
                    data-testid="whatsapp-consent-checkbox"
                    className="mt-1 h-4 w-4 rounded border-slate-300 text-rose-700 focus:ring-rose-400 accent-rose-700"
                    required
                  />
                  <label htmlFor="whatsapp-consent" className="text-xs font-sans text-slate-600 leading-normal select-none">
                    I agree to receive my free meditation and relevant Heart Glow Hub updates on WhatsApp.
                  </label>
                </div>

                {/* Prominent Benefit Message & Deliverables */}
                <div className="p-4 rounded-xl bg-gradient-to-r from-rose-50 to-amber-50/60 border border-rose-100 space-y-3 shadow-sm text-left">
                  <div className="space-y-1">
                    <h4 className="font-sans font-bold text-rose-800 text-xs flex items-center gap-1.5 uppercase tracking-wide">
                      <span role="img" aria-label="headphones" className="text-sm">🎧</span> GIFT:
                    </h4>
                    <p className="text-xs font-sans text-slate-600 leading-relaxed">
                      After you submit your name, email, and WhatsApp number, you’ll also receive a Free Somatic Healing Meditation to help you pause, breathe, and reconnect with yourself.
                    </p>
                  </div>
                  
                  <div className="border-t border-rose-100/60 pt-2 flex flex-col gap-1 text-[11px] font-sans text-slate-700">
                    <span className="font-semibold text-slate-800">You will receive BOTH:</span>
                    <div className="flex flex-col gap-1 pl-1">
                      <span className="flex items-center gap-1.5 font-medium text-emerald-800">
                        <span className="text-emerald-600 font-bold">✓</span> Personalized Relationship Blueprint
                      </span>
                      <span className="flex items-center gap-1.5 font-medium text-emerald-800">
                        <span className="text-emerald-600 font-bold">✓</span> Somatic Healing Meditation
                      </span>
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  data-testid="lead-submit-button"
                  className="w-full inline-flex items-center justify-center font-sans font-semibold tracking-wide text-base h-13 px-6 rounded-xl bg-rose-700 hover:bg-rose-800 text-white shadow-lg shadow-rose-700/20 transition-all duration-200 cursor-pointer"
                >
                  Generate My Report + Free Meditation →
                </button>
              </form>

              <div className="pt-6 border-t border-rose-100/50 flex items-start gap-3">
                <Shield className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-xs font-sans font-bold text-slate-800">Privacy Reassurance</h4>
                  <p className="text-[11px] font-sans text-slate-500 leading-normal mt-0.5">
                    Your responses are private and handled with care. Your WhatsApp number will be used to deliver the requested free meditation and, where consent has been provided, relevant Heart Glow Hub follow-up communication. We never sell or misuse your personal information.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* DYNAMIC RESULTS SCREEN */}
        {quizState === "results" && (
          <div className="space-y-12 text-left">
            
            {/* Header Result Card */}
            <div className="bg-white/85 backdrop-blur-md border-2 border-rose-100 shadow-2xl shadow-rose-900/5 rounded-3xl p-6 sm:p-10 relative overflow-hidden">
              
              {/* Top Row: Badge & Action buttons */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-rose-100/50 pb-6 mb-8 print:hidden">
                <span className={`inline-flex items-center self-start px-3 py-1 rounded-full text-xs font-sans font-semibold border ${themeClasses.badge}`}>
                  <Award className="w-3.5 h-3.5 mr-1.5" />
                  Your Relationship Blueprint Is Ready
                </span>

                <div className="flex items-center space-x-3">
                  <button
                    onClick={handlePrint}
                    data-testid="results-print-button"
                    className="inline-flex items-center justify-center text-xs font-sans font-semibold text-slate-600 hover:text-slate-900 bg-slate-100 hover:bg-slate-200/80 px-3.5 py-2 rounded-lg transition-colors cursor-pointer"
                  >
                    <Printer className="w-3.5 h-3.5 mr-1.5" />
                    Print / Save Blueprint
                  </button>

                  <button
                    onClick={resetQuiz}
                    data-testid="results-retake-button"
                    className="inline-flex items-center justify-center text-xs font-sans font-semibold text-rose-700 hover:text-rose-800 bg-rose-50 hover:bg-rose-100/80 px-3.5 py-2 rounded-lg transition-colors cursor-pointer"
                  >
                    <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                    Retake Quiz
                  </button>
                </div>
              </div>

              {/* Core Score Summary Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                
                {/* Left col: Big Score Circle */}
                <div className="lg:col-span-4 flex flex-col items-center justify-center p-6 text-center">
                  <div className={`w-40 h-40 rounded-full border-4 flex flex-col items-center justify-center shadow-lg relative ${themeClasses.scoreBg}`}>
                    {/* Tiny glow ring */}
                    <div className={`absolute inset-2 border border-dashed rounded-full opacity-60 ${themeClasses.ring}`} />
                    <span className="block text-[10px] font-sans font-bold uppercase tracking-widest text-slate-400">Resilience Score</span>
                    <span className="block font-serif text-5xl font-black mt-1" data-testid="results-score-value">{resiliencePercent}</span>
                    <span className="block text-xs font-sans font-semibold text-slate-400 border-t border-slate-200/50 pt-1 mt-1 px-4">out of 100</span>
                  </div>
                  
                  <p className="text-xs text-slate-500 font-sans mt-4 max-w-[200px] leading-relaxed">
                    Based on 10 core dimensions of relationship resilience & communication patterns.
                  </p>
                </div>

                {/* Right col: Score Interpretation & Strengths / Growth Areas */}
                <div className="lg:col-span-8 space-y-6">
                  <div className="space-y-2">
                    <span className="text-xs font-sans font-bold uppercase tracking-widest text-slate-400">
                      Personalized Analysis For {leadName}
                    </span>
                    <h2 className="text-3xl sm:text-4xl font-serif font-extrabold text-slate-900 tracking-tight animate-fade-in" data-testid="results-outcome-title">
                      {currentOutcome.title}
                    </h2>
                    <p className={`font-serif text-lg sm:text-xl font-medium italic ${themeClasses.text}`}>
                      "{currentOutcome.subtitle}"
                    </p>
                  </div>

                  <p className="text-slate-600 font-sans text-sm sm:text-base leading-relaxed">
                    Your responses suggest {currentOutcome.summary.toLowerCase().replace("the person", "you")}
                  </p>

                  {/* Strengths & Growth Areas Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                    <div className="p-4 rounded-xl bg-emerald-50/70 border border-emerald-100 text-left">
                      <span className="block text-[11px] font-sans font-bold text-emerald-800 uppercase tracking-wider">Your Relationship Strength</span>
                      <strong className="block text-slate-900 font-serif text-base mt-1">{strengthArea.name}</strong>
                      <span className="block text-xs text-slate-500 font-sans mt-0.5">{strengthArea.desc}</span>
                    </div>

                    <div className="p-4 rounded-xl bg-rose-50/70 border border-rose-100 text-left">
                      <span className="block text-[11px] font-sans font-bold text-rose-800 uppercase tracking-wider">Your Primary Growth Area</span>
                      <strong className="block text-slate-900 font-serif text-base mt-1">{growthArea.name}</strong>
                      <span className="block text-xs text-slate-500 font-sans mt-0.5">{growthArea.desc}</span>
                    </div>
                  </div>

                </div>

              </div>
            </div>

            {/* COMPLIMENTARY GIFT: FREE GUIDED SOMATIC MEDITATION AUDIO CARD */}
            <div className="bg-gradient-to-r from-rose-500 to-rose-700 text-white rounded-3xl p-6 sm:p-10 shadow-xl shadow-rose-900/10 text-left relative overflow-hidden">
              {/* Background soft glow rings */}
              <div className="absolute right-0 top-0 translate-x-12 -translate-y-12 text-rose-400 opacity-20 select-none">
                <Heart className="w-80 h-80 fill-current" />
              </div>

              <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                
                {/* Text Content */}
                <div className="lg:col-span-7 space-y-4">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-[10px] font-sans font-bold bg-white text-rose-700 tracking-wider uppercase shadow-sm">
                    <Sparkles className="w-3.5 h-3.5 mr-1 text-rose-500 animate-pulse" />
                    Complimentary Somatic Gift Unlocked
                  </span>
                  
                  <h3 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-extrabold tracking-tight text-white leading-tight">
                    Your Free Guided Somatic Meditation is Ready
                  </h3>
                  
                  <p className="text-rose-100 font-sans text-sm sm:text-base leading-relaxed">
                    Somatic work focuses on releasing stress stored in the body's nervous system. Before starting your recommended blueprint, take a few minutes to close your eyes, breathe, and listen to this exclusive, gentle guidance compiled by Pallavi Nandedkar to ground your heart.
                  </p>

                  <div className="flex flex-col sm:flex-row gap-4 pt-2 text-xs text-rose-100 font-sans">
                    <span className="flex items-center gap-1.5 bg-rose-600/50 px-3 py-1.5 rounded-lg border border-rose-400/20">
                      <Timer className="w-3.5 h-3.5 text-rose-200" />
                      Deep Breath Somatic Release
                    </span>
                    <span className="flex items-center gap-1.5 bg-rose-600/50 px-3 py-1.5 rounded-lg border border-rose-400/20">
                      <Lock className="w-3.5 h-3.5 text-rose-200" />
                      Complimentary Secure Playback
                    </span>
                  </div>
                </div>

                {/* Interactive Audio Player UI */}
                <div className="lg:col-span-5 bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 space-y-4 text-white">
                  {/* Invisible Audio Element with local and remote fallbacks */}
                  <audio
                    ref={audioRef}
                    preload="auto"
                    onLoadedMetadata={handleLoadedMetadata}
                    onTimeUpdate={handleTimeUpdate}
                    onEnded={handleEnded}
                  >
                    <source src="/somatic-healing.mp3" type="audio/mpeg" />
                    <source src="https://customer-assets-lxgj4vgw.emergentagent.net/job_interactive-quiz-hub-12/artifacts/lwag311r_My%20Audio.mp3" type="audio/mpeg" />
                  </audio>

                  <div className="flex items-center gap-4">
                    {/* Play/Pause Button */}
                    <button
                      onClick={toggleMeditationPlay}
                      data-testid="play-meditation-btn"
                      className="w-14 h-14 rounded-full bg-white text-rose-700 hover:bg-rose-50 flex items-center justify-center shadow-lg transform transition-transform active:scale-95 cursor-pointer outline-none focus:ring-2 focus:ring-rose-300"
                    >
                      {meditationPlaying ? (
                        <div className="flex gap-1.5 items-center justify-center">
                          <div className="w-1.5 h-4 bg-rose-700 rounded-full animate-bounce" style={{ animationDelay: "0s" }} />
                          <div className="w-1.5 h-4 bg-rose-700 rounded-full animate-bounce" style={{ animationDelay: "0.15s" }} />
                        </div>
                      ) : (
                        <ArrowRight className="w-6 h-6 text-rose-700 ml-0.5" />
                      )}
                    </button>

                    <div className="flex-1 space-y-1">
                      <h4 className="font-serif font-bold text-sm sm:text-base text-white">Guided Somatic Stress Release</h4>
                      <p className="text-[10px] sm:text-xs text-rose-100 font-sans">Voiced by Pallavi Nandedkar</p>
                    </div>
                  </div>

                  {/* Custom Progress Bar */}
                  <div className="space-y-1.5">
                    <div className="h-1.5 w-full bg-white/20 rounded-full overflow-hidden cursor-pointer" onClick={(e) => {
                      const audio = audioRef.current;
                      if (!audio || !audio.duration) return;
                      const rect = e.currentTarget.getBoundingClientRect();
                      const clickX = e.clientX - rect.left;
                      const width = rect.width;
                      const percent = clickX / width;
                      audio.currentTime = percent * audio.duration;
                    }}>
                      <div
                        className="h-full bg-white transition-all duration-100"
                        style={{ width: `${meditationProgress}%` }}
                      />
                    </div>

                    <div className="flex justify-between text-[10px] text-rose-100 font-sans">
                      <span>{meditationTime}</span>
                      <span>{meditationDuration}</span>
                    </div>
                  </div>
                </div>

              </div>
            </div>

            {/* Relationship Pattern & What This Means */}
            <div className="bg-white border border-stone-200/50 rounded-3xl p-6 sm:p-10 space-y-6">
              <div className="space-y-2">
                <span className="text-xs font-sans font-bold uppercase tracking-wider text-rose-700">Your Diagnostic Blueprint</span>
                <h3 className="text-2xl font-serif font-bold text-slate-900">
                  Your Relationship Pattern: <span className="underline decoration-rose-300 decoration-2">{patternInfo.pattern}</span>
                </h3>
                <p className="text-xs font-sans text-slate-500 italic">
                  A compassionate, non-diagnostic reflection of your current relational dynamics.
                </p>
              </div>

              <div className="border-t border-slate-100 pt-6 space-y-4">
                <h4 className="text-sm font-sans font-bold text-slate-800 uppercase tracking-wider">What This Means For Your Connection:</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {patternInfo.whatThisMeans.map((sentence, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-3 rounded-lg bg-stone-50/50 hover:bg-stone-50 transition-colors">
                      <div className="w-5 h-5 rounded-full bg-rose-50 text-rose-700 flex items-center justify-center font-bold text-xs flex-shrink-0 mt-0.5">
                        {idx + 1}
                      </div>
                      <p className="text-slate-600 font-sans text-sm sm:text-base leading-relaxed">
                        {sentence}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* LEVEL 1 & 2 SPECIAL PARTNER SELECTOR */}
            {(currentOutcome.level === 1 || currentOutcome.level === 2) && (
              <div className="bg-amber-50/50 border border-amber-200/60 rounded-3xl p-6 sm:p-10 space-y-6">
                <div className="space-y-2">
                  <span className="text-xs font-sans font-bold uppercase tracking-widest text-amber-800">Dynamic Recommendation Customization</span>
                  <h3 className="text-xl sm:text-2xl font-serif font-semibold text-slate-900">
                    Who do you want to better understand in your relationship?
                  </h3>
                  <p className="text-slate-600 font-sans text-sm leading-relaxed">
                    Customize your specialized next path by choosing your partner's profile to retrieve your most aligned coaching tool:
                  </p>
                </div>

                <div className="flex flex-wrap gap-3">
                  {[
                    { id: "man", label: "My male partner (Decode Man)" },
                    { id: "woman", label: "My female partner (Decode Woman)" },
                    { id: "both", label: "Both perspectives (Love Bundle)" }
                  ].map((btn) => {
                    const isSelected = partnerGender === btn.id;
                    return (
                      <button
                        key={btn.id}
                        onClick={() => setPartnerGender(btn.id)}
                        data-testid={`partner-option-${btn.id}`}
                        className={`px-5 py-3 rounded-xl border text-xs sm:text-sm font-sans font-semibold transition-all duration-200 ${
                          isSelected
                            ? "bg-amber-600 border-amber-600 text-white shadow-md shadow-amber-600/10 scale-[1.02]"
                            : "bg-white hover:bg-amber-50 border-slate-200 text-slate-700"
                        }`}
                      >
                        {btn.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Recommended Offers - Double Recommendation Structure */}
            <div className="space-y-8">
              <div className="space-y-2 text-left">
                <span className="text-xs font-sans font-bold uppercase tracking-wider text-rose-700">Your Recommended Heart Glow Hub Path</span>
                <h3 className="text-2xl sm:text-3xl font-serif font-bold text-slate-900">
                  Your Personalized Relational Roadmaps
                </h3>
                <p className="text-sm font-sans text-slate-500 max-w-3xl leading-relaxed">
                  Every secure and conscious union is built on a double engine: robust, daily communication skills combined with targeted inner healing of personal defense patterns. We recommend integrating both:
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch">
                
                {/* FOUNDATION PRODUCT: Relationship Skill Blueprint */}
                <div className="bg-white border-2 border-emerald-100 rounded-3xl p-6 sm:p-8 flex flex-col justify-between shadow-sm relative overflow-hidden group">
                  <div className="absolute right-0 top-0 translate-x-4 -translate-y-4 text-emerald-100 opacity-20">
                    <CheckCircle2 className="w-24 h-24" />
                  </div>
                  
                  <div className="space-y-4">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-sans font-bold bg-emerald-50 text-emerald-800 tracking-wider uppercase border border-emerald-100">
                      YOUR FOUNDATION (EVERYONE)
                    </span>
                    
                    <h4 className="text-xl sm:text-2xl font-serif font-bold text-slate-900">
                      {productInfo.foundation.title}
                    </h4>
                    
                    <p className="font-serif text-sm italic text-emerald-800 leading-relaxed">
                      "{productInfo.foundation.desc}"
                    </p>
                    
                    <p className="text-slate-600 font-sans text-xs sm:text-sm leading-relaxed border-t border-slate-100 pt-3">
                      {productInfo.foundation.position}
                    </p>
                  </div>

                  <div className="pt-6 border-t border-slate-100 mt-6">
                    <a
                      href={productInfo.foundation.checkoutUrl}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="results-foundation-cta"
                      className="w-full inline-flex items-center justify-center font-sans font-bold text-xs sm:text-sm h-11 px-4 rounded-xl bg-emerald-800 hover:bg-emerald-900 text-white shadow-md transition-colors"
                    >
                      Enroll in Foundation Program
                      <ArrowRight className="w-4 h-4 ml-1.5" />
                    </a>
                  </div>
                </div>

                {/* SPECIALIZED NEXT PATH (Based on score and pattern) */}
                <div className="bg-white border-2 border-rose-100 rounded-3xl p-6 sm:p-8 flex flex-col justify-between shadow-sm relative overflow-hidden group">
                  <div className="absolute right-0 top-0 translate-x-4 -translate-y-4 text-rose-100 opacity-20">
                    <Sparkles className="w-24 h-24" />
                  </div>

                  <div className="space-y-4">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-sans font-bold bg-rose-50 text-rose-800 tracking-wider uppercase border border-rose-100">
                      YOUR PERSONALIZED NEXT PATH
                    </span>

                    <h4 className="text-xl sm:text-2xl font-serif font-bold text-slate-900">
                      {productInfo.secondary.title}
                    </h4>

                    <p className="font-serif text-sm italic text-rose-800 leading-relaxed">
                      "{productInfo.secondary.desc}"
                    </p>

                    <p className="text-slate-600 font-sans text-xs sm:text-sm leading-relaxed border-t border-slate-100 pt-3">
                      Tailored specifically to your score of <strong className="text-rose-700">{resiliencePercent}/100</strong> and your primary growth need in <strong className="text-slate-800 font-serif">{growthArea.name}</strong>.
                    </p>
                  </div>

                  <div className="pt-6 border-t border-slate-100 mt-6">
                    <a
                      href={productInfo.secondary.checkoutUrl}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="results-cta-button"
                      className="w-full inline-flex items-center justify-center font-sans font-bold text-xs sm:text-sm h-11 px-4 rounded-xl bg-rose-700 hover:bg-rose-800 text-white shadow-md transition-colors"
                    >
                      Explore Your Recommended Path
                      <ArrowRight className="w-4 h-4 ml-1.5" />
                    </a>
                  </div>
                </div>

              </div>

              {/* INTEGRATION POSITIONING HIGHLIGHT BOX */}
              <div className="bg-stone-50 border border-stone-200/60 rounded-2xl p-5 text-left flex items-start gap-4">
                <Compass className="w-6 h-6 text-rose-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h5 className="text-xs font-sans font-bold text-slate-800 uppercase tracking-wider">How These Paths Support Each Other:</h5>
                  <p className="text-xs sm:text-sm font-sans text-slate-600 leading-relaxed mt-1">
                    {productInfo.positioning}
                  </p>
                </div>
              </div>

            </div>

            {/* DIAMOND POSITIONING (Advanced upgrade shown gracefully for all levels as alternative next evolution) */}
            <div className="bg-gradient-to-r from-stone-900 to-slate-950 text-slate-100 rounded-3xl p-6 sm:p-10 space-y-6 relative overflow-hidden shadow-xl border border-stone-800">
              <div className="absolute right-0 top-0 translate-x-8 -translate-y-8 text-stone-800/40 select-none">
                <Sparkles className="w-48 h-48" />
              </div>

              <div className="relative space-y-4 text-left max-w-4xl">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-sans font-bold bg-stone-800 text-stone-300 tracking-widest uppercase border border-stone-700">
                  Advanced Pathway Upgrade
                </span>

                <h3 className="text-2xl sm:text-3xl font-serif font-bold text-white">
                  The Relationship Renaissance — DIAMOND
                </h3>

                <p className="text-slate-300 font-sans text-sm sm:text-base leading-relaxed">
                  For those ready to move beyond foundational healing into deeper alignment, embodiment, and conscious relationship energy work. This advanced upgrade provides comprehensive integration.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs sm:text-sm text-slate-300 pt-2">
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-400" />
                    Divine Masculine & Feminine Integration
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-400" />
                    Heart and Throat Chakra Opening Work
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-400" />
                    Intuition and Inner Awareness Practices
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-400" />
                    Advanced Conscious Relationship Embodiment
                  </div>
                </div>

                <div className="pt-4">
                  <a
                    href="https://pretty.heartglowhub.net/v2/auth/sign-up?product=trr_diamond"
                    target="_blank"
                    rel="noreferrer"
                    data-testid="results-diamond-cta"
                    className="inline-flex items-center justify-center font-sans font-bold text-xs sm:text-sm h-11 px-5 rounded-lg bg-stone-800 hover:bg-stone-700 text-white border border-stone-700 transition-colors"
                  >
                    Explore Diamond Advanced Pathway
                    <ChevronRight className="w-4 h-4 ml-1.5" />
                  </a>
                </div>
              </div>
            </div>

            {/* NEXT LEVEL 4 VALUE-ADD ACTIONS BLOCK */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-12 print:hidden">
              
              {/* FEATURE 1: DYNAMIC WHATSAPP DELIVERY & FEATURE 2: SOCIAL BADGE */}
              <div className="space-y-8">
                
                {/* WHATSAPP DELIVERY CARD */}
                <div className="bg-white border border-stone-200/60 rounded-3xl p-6 sm:p-8 space-y-6 text-left shadow-sm">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-emerald-50 text-emerald-800 flex items-center justify-center">
                        <Phone className="w-5 h-5" />
                      </div>
                      <div>
                        <h4 className="font-serif font-bold text-slate-900 text-lg">WhatsApp Delivery</h4>
                        <p className="text-xs text-slate-500 font-sans">Receive your 10-page relationship blueprint instantly</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setWhatsAppDeliveryActive(!whatsAppDeliveryActive)}
                      data-testid="whatsapp-toggle"
                      className={`w-12 h-6 rounded-full p-1 transition-colors duration-200 focus:outline-none ${
                        whatsAppDeliveryActive ? "bg-emerald-600" : "bg-slate-200"
                      }`}
                    >
                      <div
                        className={`bg-white w-4 h-4 rounded-full shadow-md transform transition-transform duration-200 ${
                          whatsAppDeliveryActive ? "translate-x-6" : "translate-x-0"
                        }`}
                      />
                    </button>
                  </div>

                  {whatsAppDeliveryActive && (
                    <div className="space-y-4 pt-2 border-t border-slate-100 animate-fade-in">
                      {!whatsAppSubmitted ? (
                        <div className="space-y-3">
                          <label className="block text-xs font-sans font-bold text-slate-700 uppercase tracking-wider">
                            Your WhatsApp Number
                          </label>
                          <div className="flex gap-2">
                            <input
                              type="tel"
                              placeholder="+1 (555) 000-0000"
                              value={whatsAppNumber}
                              onChange={(e) => setWhatsAppNumber(e.target.value)}
                              data-testid="whatsapp-input"
                              className="flex-1 font-sans px-4 py-2.5 rounded-xl border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50 text-sm"
                            />
                            <button
                              onClick={() => {
                                if (!whatsAppNumber.trim()) {
                                  toast.error("Please enter a valid phone number.");
                                  return;
                                }
                                setWhatsAppSubmitted(true);
                                toast.success("Secure WhatsApp delivery initiated successfully.");
                              }}
                              data-testid="whatsapp-submit-btn"
                              className="px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-sans font-semibold text-xs tracking-wider uppercase transition-colors"
                            >
                              Deliver Report
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-100 text-emerald-900 space-y-2 text-sm font-sans">
                          <p className="font-bold flex items-center gap-1.5">
                            <Check className="w-4 h-4 text-emerald-600" />
                            Secure Transmission Active
                          </p>
                          <p className="text-xs text-emerald-800">
                            We are compiling your 10-page diagnostic blueprint for {leadName}. Your report will arrive shortly at <strong>{whatsAppNumber}</strong> via secure whitelabel routing.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* SOCIAL SHARING BADGE CARD */}
                <div className="bg-white border border-stone-200/60 rounded-3xl p-6 sm:p-8 space-y-6 text-left shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-rose-50 text-rose-700 flex items-center justify-center">
                      <Share2 className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-serif font-bold text-slate-900 text-lg">Relationship Resilience Badge</h4>
                      <p className="text-xs text-slate-500 font-sans">Share your self-awareness badge & invite growth</p>
                    </div>
                  </div>

                  {/* Aesthetic Glowing Badge Renders */}
                  <div className="flex flex-col sm:flex-row items-center gap-6 p-4 rounded-2xl bg-stone-50 border border-stone-200/40">
                    <div className={`w-28 h-28 rounded-full border-4 flex flex-col items-center justify-center text-center shadow-lg relative ${themeClasses.scoreBg} select-none`}>
                      <div className={`absolute inset-1.5 border border-dashed rounded-full opacity-40 ${themeClasses.ring}`} />
                      <Heart className="w-6 h-6 text-rose-600 animate-pulse" />
                      <span className="text-[10px] font-sans font-bold uppercase tracking-wider text-slate-500 mt-1">Certified</span>
                      <span className="text-[9px] font-sans text-slate-400 font-semibold">{patternInfo.pattern.split(" ")[0]}</span>
                    </div>

                    <div className="flex-1 space-y-3 text-center sm:text-left">
                      <p className="text-xs text-slate-600 font-sans leading-relaxed">
                        Celebrate committing to deeper self-awareness! Share your personalized relationship resilience badge with your partner or friends.
                      </p>
                      <button
                        onClick={() => {
                          const shareMsg = `I just mapped my Relationship Resilience Score with Heart Glow Hub and received my custom '${patternInfo.pattern}' Blueprint! Take the free 3-minute quiz by Pallavi Nandedkar here: https://pretty.heartglowhub.net/`;
                          if (navigator.clipboard && navigator.clipboard.writeText) {
                            navigator.clipboard.writeText(shareMsg)
                              .then(() => {
                                toast.success("Aesthetic sharing blueprint copied to clipboard. Paste anywhere to invite partner growth!");
                              })
                              .catch((err) => {
                                console.warn("Clipboard copy failed, using fallback:", err);
                                fallbackCopy(shareMsg);
                              });
                          } else {
                            fallbackCopy(shareMsg);
                          }
                        }}
                        data-testid="share-badge-btn"
                        className="inline-flex items-center justify-center text-xs font-sans font-semibold text-rose-700 hover:text-rose-800 bg-rose-50 hover:bg-rose-100/80 px-4 py-2 rounded-xl transition-colors"
                      >
                        Copy Shareable Blueprint Link
                      </button>
                    </div>
                  </div>
                </div>

              </div>

              {/* FEATURE 3: SOMATIC TIMER */}
              <div className="bg-white border border-stone-200/60 rounded-3xl p-6 sm:p-8 space-y-6 text-left shadow-sm flex flex-col justify-between">
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-rose-50 text-rose-700 flex items-center justify-center">
                      <Timer className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-serif font-bold text-slate-900 text-lg">Interactive Somatic Centering Timer</h4>
                      <p className="text-xs text-slate-500 font-sans">Nervous system grounding before starting your recommendations</p>
                    </div>
                  </div>

                  <p className="text-slate-600 font-sans text-xs sm:text-sm leading-relaxed">
                    Take a moment to align your body with your heart. Follow our somatic 1-minute box-breathing timer below (4s Inhale, 4s Hold, 4s Exhale) to release daily tension.
                  </p>
                </div>

                <div className="flex flex-col items-center justify-center py-4 text-center space-y-6">
                  {/* Timer Pulse Circle */}
                  <div className="relative flex items-center justify-center">
                    <div className={`w-36 h-38 rounded-full border-4 border-rose-100 flex flex-col items-center justify-center transition-all duration-1000 ${
                      timerActive && breathPhase === "Inhale" ? "scale-[1.15] bg-rose-50 border-rose-300 ring-8 ring-rose-50" :
                      timerActive && breathPhase === "Exhale" ? "scale-[0.9] bg-stone-50 border-slate-200" :
                      "scale-[1.0] bg-white"
                    }`}>
                      <span className="text-[10px] font-sans font-bold uppercase tracking-widest text-slate-400">
                        {timerActive ? breathPhase : "Ready"}
                      </span>
                      <span className="font-serif text-3xl font-extrabold text-slate-900 mt-1">
                        {timeLeft}s
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setTimerActive(!timerActive)}
                      data-testid="timer-toggle-btn"
                      className="px-5 h-10 rounded-xl bg-rose-700 hover:bg-rose-800 text-white font-sans font-semibold text-xs tracking-wider uppercase transition-colors"
                    >
                      {timerActive ? "Pause Timer" : timeLeft === 0 ? "Restart Timer" : "Start Centering"}
                    </button>
                    <button
                      onClick={() => {
                        setTimerActive(false);
                        setTimeLeft(60);
                        setBreathPhase("Inhale");
                        toast.info("Timer reset successfully.");
                      }}
                      data-testid="timer-reset-btn"
                      className="px-4 h-10 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-sans font-semibold text-xs tracking-wider uppercase transition-colors"
                    >
                      Reset
                    </button>
                  </div>
                </div>
              </div>

            </div>

            {/* FEATURE 4: COUPLE COMPARISON ALIGNMENT PANEL */}
            <div className="bg-white border border-stone-200/60 rounded-3xl p-6 sm:p-10 space-y-6 text-left shadow-sm">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-rose-50 text-rose-700 flex items-center justify-center">
                    <Users className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-serif font-bold text-slate-900 text-xl">Is Your Partner on the Same Page?</h4>
                    <p className="text-xs text-slate-500 font-sans">Compare patterns and evaluate connection alignment</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowComparison(!showComparison)}
                  data-testid="comparison-toggle"
                  className="px-4 py-2 rounded-xl border border-slate-200 hover:bg-slate-50 text-xs font-sans font-semibold text-slate-700 transition-colors"
                >
                  {showComparison ? "Hide Analyzer" : "Expand Alignment Analyzer"}
                </button>
              </div>

              {showComparison && (
                <div className="space-y-8 pt-6 border-t border-slate-100 animate-fade-in">
                  <p className="text-slate-600 font-sans text-xs sm:text-sm leading-relaxed max-w-4xl">
                    Estimate or enter your partner's scores below across 3 foundational dimensions. We'll dynamically compute a mutual alignment comparison diagram and generate custom-tailored recommendations for your connection.
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
                    
                    {/* Partner Sliders */}
                    <div className="space-y-6">
                      <div className="space-y-2">
                        <div className="flex justify-between text-xs font-sans font-semibold text-slate-700">
                          <span>Communication & Direct Expression</span>
                          <span className="text-rose-700">Level {partnerCommScore}</span>
                        </div>
                        <input
                          type="range"
                          min="1"
                          max="4"
                          value={partnerCommScore}
                          onChange={(e) => setPartnerCommScore(parseInt(e.target.value, 10))}
                          data-testid="partner-comm-slider"
                          className="w-full accent-rose-700"
                        />
                      </div>

                      <div className="space-y-2">
                        <div className="flex justify-between text-xs font-sans font-semibold text-slate-700">
                          <span>Trigger Management & De-escalation</span>
                          <span className="text-rose-700">Level {partnerTriggerScore}</span>
                        </div>
                        <input
                          type="range"
                          min="1"
                          max="4"
                          value={partnerTriggerScore}
                          onChange={(e) => setPartnerTriggerScore(parseInt(e.target.value, 10))}
                          data-testid="partner-trigger-slider"
                          className="w-full accent-rose-700"
                        />
                      </div>

                      <div className="space-y-2">
                        <div className="flex justify-between text-xs font-sans font-semibold text-slate-700">
                          <span>Intimacy Capacity & Shared Presence</span>
                          <span className="text-rose-700">Level {partnerIntimacyScore}</span>
                        </div>
                        <input
                          type="range"
                          min="1"
                          max="4"
                          value={partnerIntimacyScore}
                          onChange={(e) => setPartnerIntimacyScore(parseInt(e.target.value, 10))}
                          data-testid="partner-intimacy-slider"
                          className="w-full accent-rose-700"
                        />
                      </div>
                    </div>

                    {/* Calculated alignment chart / metrics */}
                    <div className="p-6 rounded-2xl bg-stone-50 border border-stone-200/40 space-y-6">
                      <div className="text-center">
                        <span className="block text-[11px] font-sans font-bold text-slate-400 uppercase tracking-widest">Calculated Alignment</span>
                        <span className="block font-serif text-4xl font-extrabold text-slate-900 mt-1">
                          {Math.max(40, 100 - Math.round(((Math.abs(partnerCommScore - 3) + Math.abs(partnerTriggerScore - 3) + Math.abs(partnerIntimacyScore - 3)) / 9) * 50))}%
                        </span>
                      </div>

                      <div className="space-y-3">
                        <h5 className="text-xs font-sans font-bold text-slate-800 uppercase tracking-wider">Alignment Dynamics Recommendation:</h5>
                        <p className="text-xs text-slate-600 font-sans leading-relaxed">
                          Your pattern compared with your estimates suggests that you both possess strong capacity, though differing styles in managing triggers could cause minor friction. Exploring the <strong>Relationship Skill Blueprint</strong> together is highly recommended to establish unified de-escalation rules.
                        </p>
                      </div>
                    </div>

                  </div>
                </div>
              )}
            </div>

          </div>
        )}

        {/* CREATOR INFO SECTION (Always visible below landing, or dynamically on landing/results) */}
        <section id="mentor-section" className="mt-28 border-t border-rose-100/50 pt-24 text-left">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            <div className="lg:col-span-5 relative">
              <div className="absolute inset-0 bg-rose-200/10 rounded-3xl blur-3xl" />
              <div className="relative rounded-3xl overflow-hidden border border-rose-100 shadow-xl">
                <img
                  src="https://images.unsplash.com/photo-1765813102971-47144ca56d03?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHw3fHxwZWFjZWZ1bCUyMHdvbWFuJTIwd2FybXRoJTIwc2VyZW5lJTIwcmVmbGVjdGlvbiUyMHBvcnRyYWl0fGVufDB8fHx8MTc4OTA0MjIzOHww&ixlib=rb-4.1.0&q=85"
                  alt="Pallavi Nandedkar, a friendly and warm professional relationship expert smiling, with soft background lighting"
                  className="w-full h-[400px] sm:h-[480px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/40 via-transparent to-transparent" />
              </div>
            </div>

            <div className="lg:col-span-7 space-y-8">
              <div className="space-y-4">
                <span className="text-xs font-sans font-bold uppercase tracking-widest text-rose-700">
                  Lead Relationship Guide
                </span>
                <h2 className="text-3xl sm:text-4xl font-serif font-semibold text-slate-900 tracking-tight">
                  Meet Pallavi Nandedkar
                </h2>
                <p className="text-base sm:text-lg font-sans text-slate-600 leading-relaxed">
                  Pallavi Nandedkar is the founder of Heart Glow Hub, a relationship expert and mindset coach with 10 years of experience guiding couples and individuals toward healthier relationships, emotional clarity and stronger connections. With a medical background, she brings a structured and compassionate approach to relationship and mindset work.
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-rose-50 text-rose-700 flex items-center justify-center flex-shrink-0 mt-1">
                    <Star className="w-3.5 h-3.5 fill-current" />
                  </div>
                  <div>
                    <h4 className="text-sm font-sans font-bold text-slate-900">Relationship & Mindset Coaching</h4>
                    <p className="text-xs text-slate-500 font-sans mt-0.5">Practical and compassionate guidance toward deeper understanding and healthier bonds.</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-rose-50 text-rose-700 flex items-center justify-center flex-shrink-0 mt-1">
                    <Star className="w-3.5 h-3.5 fill-current" />
                  </div>
                  <div>
                    <h4 className="text-sm font-sans font-bold text-slate-900">Structured Relationship Tools</h4>
                    <p className="text-xs text-slate-500 font-sans mt-0.5">Highly recognized for pioneering the 3-minute Relationship Diagnostic Blueprint.</p>
                  </div>
                </div>
              </div>

              <div className="pt-4">
                <blockquote className="border-l-4 border-rose-400 pl-4 italic text-slate-600 font-serif text-lg leading-relaxed">
                  "Understanding your patterns is the first step toward lasting harmony. By shifting how we connect and communicate, we can build relationships rooted in trust and clarity."
                </blockquote>
              </div>
            </div>

          </div>
        </section>

        {/* TESTIMONIALS SECTION */}
        <section className="mt-28 border-t border-rose-100/50 pt-24 text-left">
          <div className="space-y-12">
            <div className="max-w-3xl space-y-4">
              <span className="text-xs font-sans font-bold uppercase tracking-widest text-rose-700">
                Success Stories
              </span>
              <h2 className="text-3xl sm:text-4xl font-serif font-semibold text-slate-900 tracking-tight">
                Hearts Reconnected & Healed
              </h2>
              <p className="text-slate-600 font-sans text-base">
                Discover how real people have used their customized diagnostics to completely rewrite their relationship dynamics and construct safe, resilient unions.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              
              <div className="bg-white border border-stone-200/50 rounded-2xl p-6 sm:p-8 space-y-6 flex flex-col justify-between shadow-sm hover:shadow-md transition-shadow">
                <div className="space-y-4">
                  <div className="flex items-center gap-0.5 text-amber-500">
                    <Star className="w-4.5 h-4.5 fill-current" />
                    <Star className="w-4.5 h-4.5 fill-current" />
                    <Star className="w-4.5 h-4.5 fill-current" />
                    <Star className="w-4.5 h-4.5 fill-current" />
                    <Star className="w-4.5 h-4.5 fill-current" />
                  </div>
                  <p className="text-slate-600 font-sans text-sm sm:text-base leading-relaxed italic">
                    "I finally understood some of the patterns I was repeating in my relationships. The assessment gave me a completely different perspective and helped me look at my situation with more clarity."
                  </p>
                </div>
                <div className="flex items-center gap-3 border-t border-stone-100 pt-4 mt-auto">
                  <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center font-bold text-rose-800 font-serif">
                    M
                  </div>
                  <div>
                    <h4 className="text-xs font-sans font-bold text-slate-900">Manju</h4>
                    <span className="text-[10px] text-slate-500 font-sans">Quiz Participant</span>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-stone-200/50 rounded-2xl p-6 sm:p-8 space-y-6 flex flex-col justify-between shadow-sm hover:shadow-md transition-shadow">
                <div className="space-y-4">
                  <div className="flex items-center gap-0.5 text-amber-500">
                    <Star className="w-4.5 h-4.5 fill-current" />
                    <Star className="w-4.5 h-4.5 fill-current" />
                    <Star className="w-4.5 h-4.5 fill-current" />
                    <Star className="w-4.5 h-4.5 fill-current" />
                    <Star className="w-4.5 h-4.5 fill-current" />
                  </div>
                  <p className="text-slate-600 font-sans text-sm sm:text-base leading-relaxed italic">
                    "The questions made me pause and think about how I communicate and respond emotionally. It gave me practical clarity about what I can work on."
                  </p>
                </div>
                <div className="flex items-center gap-3 border-t border-stone-100 pt-4 mt-auto">
                  <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center font-bold text-rose-800 font-serif">
                    A
                  </div>
                  <div>
                    <h4 className="text-xs font-sans font-bold text-slate-900">Ashok</h4>
                    <span className="text-[10px] text-slate-500 font-sans">Quiz Participant</span>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-stone-200/50 rounded-2xl p-6 sm:p-8 space-y-6 flex flex-col justify-between shadow-sm hover:shadow-md transition-shadow">
                <div className="space-y-4">
                  <div className="flex items-center gap-0.5 text-amber-500">
                    <Star className="w-4.5 h-4.5 fill-current" />
                    <Star className="w-4.5 h-4.5 fill-current" />
                    <Star className="w-4.5 h-4.5 fill-current" />
                    <Star className="w-4.5 h-4.5 fill-current" />
                    <Star className="w-4.5 h-4.5 fill-current" />
                  </div>
                  <p className="text-slate-600 font-sans text-sm sm:text-base leading-relaxed italic">
                    "A beautifully paced relationship assessment. It felt like a gentle mirror showing me exactly where communication blocks occur and how to approach my partner more intentionally."
                  </p>
                </div>
                <div className="flex items-center gap-3 border-t border-stone-100 pt-4 mt-auto">
                  <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center font-bold text-rose-800 font-serif">
                    N
                  </div>
                  <div>
                    <h4 className="text-xs font-sans font-bold text-slate-900">Neha</h4>
                    <span className="text-[10px] text-slate-500 font-sans">Quiz Participant</span>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* TRUST ACCORDION (FAQ) */}
        <section className="mt-28 border-t border-rose-100/50 pt-24 text-left">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            
            <div className="lg:col-span-4 space-y-4">
              <span className="text-xs font-sans font-bold uppercase tracking-widest text-rose-700">
                Have Questions?
              </span>
              <h2 className="text-2xl sm:text-3xl font-serif font-semibold text-slate-900 tracking-tight">
                Frequently Asked Inquiries
              </h2>
              <p className="text-slate-500 font-sans text-sm leading-relaxed">
                Learn more about the science, security, and next steps of the Heart Glow Hub Relational Assessment.
              </p>
            </div>

            <div className="lg:col-span-8 space-y-4">
              {[
                {
                  q: "How does the scoring system work?",
                  a: "The assessment contains 10 questions focusing on key dimensions like communication, triggers, boundary-setting, and emotional presence. Each question has four choices carrying points (0, 1, 3, or 4 respectively). The total score ranges from 0 to 40, mapping to four specific emotional outcomes."
                },
                {
                  q: "Are my results kept completely private?",
                  a: "Yes. Your privacy is sacred to us. All quiz answers are processed entirely on your device and cached in your browser. We never sell, rent, or distribute your email or name."
                },
                {
                  q: "What is TagMango?",
                  a: "TagMango is our secure partner platform where our premium workshops, masterclasses, and digital workbooks are securely hosted. After receiving your scores, you will be offered custom links to continue your journey inside TagMango."
                },
                {
                  q: "Can my partner and I take it together?",
                  a: "Absolutely! In fact, we highly recommend taking it individually, then getting together in a comfortable space to compare your scores, outcome brackets, and custom insights side-by-side."
                }
              ].map((faq, index) => {
                const isOpen = faqOpenIndex === index;
                return (
                  <div
                    key={index}
                    className="border border-stone-200 bg-white rounded-xl overflow-hidden transition-all shadow-sm"
                  >
                    <button
                      onClick={() => toggleFaq(index)}
                      data-testid={`faq-toggle-${index}`}
                      className="w-full flex items-center justify-between p-5 text-left font-serif font-semibold text-slate-950 text-base sm:text-lg focus:outline-none cursor-pointer"
                    >
                      <span>{faq.q}</span>
                      <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
                    </button>
                    {isOpen && (
                      <div className="p-5 border-t border-stone-100 bg-slate-50/50 text-slate-600 font-sans text-sm sm:text-base leading-relaxed">
                        {faq.a}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

          </div>
        </section>

      </main>

      {/* FOOTER */}
      <footer className="bg-slate-950 text-slate-400 border-t border-slate-900 mt-32 py-16 print:bg-white print:text-black print:border-none print:mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
            
            <div className="md:col-span-5 space-y-4 text-left">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-10 rounded-full bg-rose-600 flex items-center justify-center">
                  <Heart className="w-5 h-5 text-white" />
                </div>
                <span className="font-serif text-lg font-bold text-white tracking-tight">Heart Glow Hub</span>
              </div>
              <p className="text-xs leading-relaxed max-w-sm">
                Empowering individuals and couples around the globe to construct deep emotional safety, heal attachment trauma, and welcome securely aligned, lifelong love.
              </p>
            </div>

            <div className="md:col-span-3 text-left space-y-3">
              <h4 className="text-xs font-sans font-bold text-white uppercase tracking-wider">Offerings</h4>
              <ul className="space-y-2 text-xs">
                <li><a href="https://pretty.heartglowhub.net" className="hover:text-white transition-colors">Start Assessment</a></li>
                <li><a href="https://pretty.heartglowhub.net/v2/auth/sign-up" className="hover:text-white transition-colors">Masterclasses</a></li>
                <li><a href="https://pretty.heartglowhub.net/v2/auth/sign-up" className="hover:text-white transition-colors">Private Cohorts</a></li>
                <li><a href="https://pretty.heartglowhub.net/v2/auth/sign-up" className="hover:text-white transition-colors">Mentorship</a></li>
              </ul>
            </div>

            <div className="md:col-span-4 text-left space-y-3">
              <h4 className="text-xs font-sans font-bold text-white uppercase tracking-wider">Privacy & Legal</h4>
              <p className="text-[11px] leading-relaxed">
                By taking this assessment, you agree to our Terms of Use and Privacy Policy. All inputs are completely confidential.
              </p>
              <div className="flex space-x-4 text-xs font-sans">
                <a href="https://pretty.heartglowhub.net" className="underline hover:text-white">Terms of Use</a>
                <a href="https://pretty.heartglowhub.net" className="underline hover:text-white">Privacy Policy</a>
              </div>
            </div>

          </div>

          {/* Emergency Crisis Support Disclaimer (Highly critical/ethical for relationship/wellness brands) */}
          <div className="p-5 bg-rose-950/20 border border-rose-900/30 rounded-2xl text-left" data-testid="disclaimer-alert">
            <h4 className="text-xs font-sans font-bold text-rose-200 tracking-wider uppercase flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-rose-500" />
              Emotional Wellness Alert
            </h4>
            <p className="text-[11px] text-slate-400 mt-1.5 leading-relaxed">
              Relationship healing work can bring up deep somatic triggers. If you are experiencing acute distress, domestic instability, or safety concerns, please contact your local support hotline immediately or access professional therapy. You are never alone.
            </p>
          </div>

          <div className="border-t border-slate-900 pt-8 flex flex-col sm:flex-row items-center justify-between text-[11px] font-sans">
            <p>© {new Date().getFullYear()} Heart Glow Hub. All rights reserved. Managed under TagMango Studio partner platform.</p>
            <p className="mt-4 sm:mt-0">Built with ❤️ by Mango AI</p>
          </div>

        </div>
      </footer>
    </div>
  );
}